import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { useLocale } from "../i18n/LocaleContext";
import { TREATMENT_IMAGES_EXTRA } from "../data/treatments";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Lightbox } from "../components/Lightbox";

const STUDIO_GALLERY = [
  { key: "lounge2", src: TREATMENT_IMAGES_EXTRA.lounge2, alt: "Empfangsbereich" },
  { key: "treatment", src: TREATMENT_IMAGES_EXTRA.treatmentRoom, alt: "Behandlungsraum" },
  { key: "massage", src: TREATMENT_IMAGES_EXTRA.massageRoom, alt: "Massage im Studio" },
  { key: "lounge", src: TREATMENT_IMAGES_EXTRA.lounge, alt: "Lounge" },
];

export default function Studio() {
  const { t, link } = useLocale();
  const [lightbox, setLightbox] = useState(null);
  return (
    <div data-testid="studio-page">
      <section className="mx-auto max-w-[1600px] px-6 pb-16 pt-40 sm:px-10 lg:px-16 lg:pt-52">
        <Reveal>
          <Eyebrow>{t("studio.eyebrow")}</Eyebrow>
          <h1 className="mt-6 font-serif text-5xl font-light text-imperium-black sm:text-6xl">{t("studio.title")}</h1>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-imperium-black/70">{t("studio.intro")}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
        <Reveal>
          <div className="overflow-hidden">
            <img src={TREATMENT_IMAGES_EXTRA.room2} alt="IMPERIUM Studio" data-testid="studio-hero-image" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.03] lg:aspect-[16/10]" />
          </div>
        </Reveal>
      </section>

      <section className="mx-auto grid max-w-[1600px] gap-14 px-6 py-24 sm:px-10 lg:grid-cols-12 lg:px-16 lg:py-32">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="max-w-lg font-serif text-2xl font-light leading-relaxed text-imperium-black sm:text-3xl" data-testid="studio-preparing">{t("studio.preparing")}</p>
          </Reveal>
        </div>
        <div className="lg:col-span-4 lg:col-start-9">
          <Reveal delay={0.12}>
            <div className="border-t border-imperium-champagne/60 pt-6">
              <p className="text-[11px] font-label uppercase tracking-[0.3em] text-imperium-bronze">{t("studio.locationLabel")}</p>
              <p className="mt-4 flex items-center gap-3 font-serif text-2xl font-light text-imperium-black" data-testid="studio-location">
                <MapPin className="h-5 w-5 text-imperium-gold" />
                {t("studio.location")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 pb-24 sm:px-10 lg:px-16 lg:pb-32">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {STUDIO_GALLERY.map((img, i) => (
            <Reveal key={img.key} delay={0.08 * i} className={i === 1 ? "lg:mt-16" : ""}>
              <button
                type="button"
                data-testid={`studio-gallery-${i}`}
                aria-label={img.alt}
                onClick={() => setLightbox(i)}
                className="group block w-full cursor-zoom-in overflow-hidden"
              >
                <img src={img.src} alt={img.alt} loading="lazy" className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              </button>
            </Reveal>
          ))}
        </div>
        <Lightbox images={STUDIO_GALLERY} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />
      </section>

      <section className="bg-imperium-brown">
        <div className="mx-auto max-w-3xl px-6 py-24 text-center lg:py-32">
          <Reveal>
            <h2 className="font-serif text-4xl font-light text-imperium-white sm:text-5xl">{t("studio.ctaTitle")}</h2>
            <p className="mx-auto mt-6 max-w-md text-base font-light leading-relaxed text-imperium-white/70">{t("studio.ctaText")}</p>
            <Link
              to={link("contact")}
              data-testid="studio-cta"
              className="mt-10 inline-block border border-imperium-champagne px-8 py-4 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-champagne transition-colors duration-300 hover:bg-imperium-champagne hover:text-imperium-brown"
            >
              {t("cta.book")}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
